<template>
  <div class="pb-52">
    <transition
      name="fade"
      mode="out-in">
      <div v-if="loaded">
        <div
          v-if="noContent"
          class="container">
          <h1>Welcome to the OPUS Learning Zone.</h1>
          <p v-if="isSolo">
            You will be able to access all of your OPUS training courses from here. Please check back again soon, or
            after you have received confirmation from OPUS that your training has been set up
          </p>
          <p v-else>
            You will be able to access all of your OPUS training courses from here. Please check back again soon or once
            your manager has set up your training.
          </p>
        </div>
        <div v-else>
          <div class="flex flex-row flex-wrap sm:flex-no-wrap justify-between items-end mb-4 md:mb-8">
            <div class="flex flex-row flex-wrap sm:flex-no-wrap justify-between">
              <div class="mr-8">
                <label
                  class="control-label"
                  for="title-filter"
                  >Filter by Course Title: </label
                ><input
                  type="text"
                  v-model="filters.title"
                  id="title-filter"
                  class="form-control"
                  autocomplete="off"
                  placeholder="Start typing..." />
              </div>
              <div class="mr-8">
                <label
                  class="control-label"
                  for="status-filter"
                  >Filter by Progress:</label
                >
                <select
                  v-model="filters.status"
                  id="status-filter"
                  class="form-control">
                  <option>N/A</option>
                  <option
                    v-for="option in statusOptions"
                    :key="option.if"
                    :value="option.id">
                    {{ option.label }}
                  </option>
                </select>
              </div>
              <div v-if="$store.state.learningZoneTab === 'general'">
                <label
                  class="control-label"
                  for="deliverable-filter"
                  >Filter by Deliverable:</label
                >
                <select
                  v-model="filters.deliverable"
                  id="deliverable-filter"
                  class="form-control">
                  <option value="">N/A</option>
                  <option value="1">E-Learning</option>
                  <option value="2">Distance Learning</option>
                  <option value="3">Virtual Learning</option>
                </select>
              </div>
            </div>
            <div class="text-right mt-4 sm:mt-0 flex flex-row justify-start items-center">
              <span class="mr-2 inline-block">Layout: </span>
              <!-- prettier-ignore -->
              <button
                @click.prevent="ScoLayout = 'ScoCard'"
                class="mr-2 focus:outline-none border-primary-pastle border-2 rounded-md hover:border-secondary
                 hover:bg-white duration-200"
                :class="{ 'border-secondary border-2 text-secondary': ScoLayout === 'ScoCard' }">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 
                                        012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0
                                         012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2
                                          0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg></button

              >
              <!-- prettier-ignore -->
              <button
                @click.prevent="ScoLayout = 'ScoRow'"
                class="mr-2 focus:outline-none border-primary-pastle border-2 rounded-md hover:border-secondary
                 hover:bg-white duration-200"
                :class="{ 'border-secondary border-2 text-secondary': ScoLayout !== 'ScoCard' }">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>

          <icon-legend
            class="-mx-12"
            :hide-certificate-icon="hideCertificateInLegend" />

          <p
            v-if="userGroups && userGroups.length === 1"
            class="my-2 text-sm">
            {{ userGroups[0].title }}
            <span v-if="userSubgroups && userSubgroups.length === 1">/ {{ userSubgroups[0].title }}</span>
          </p>

          <transition
            name="fade"
            mode="out-in">
            <div
              v-if="$store.state.learningZoneTab === 'general'"
              key="generalCourses">
              <div v-if="generalCourses && generalCourses.length">
                <h2 class="mt-4">Training</h2>
                <div
                  v-if="filteredCourses.length > 0"
                  class="sm:-mx-12">
                  <transition
                    name="quick-fade"
                    mode="out-in">
                    <div
                      v-if="ScoLayout === 'ScoCard'"
                      class="flex flex-row flex-wrap px-2 md:px-6">
                      <transition-group name="list">
                        <div
                          v-for="course in filteredCourses"
                          :key="course[0].sco_user_id"
                          class="flex-none"
                          :class="cardWrapperClasses(course)">
                          <course-card :course="course"></course-card>
                        </div>
                      </transition-group>
                    </div>
                    <div
                      v-else
                      class="flex flex-col px-4 md:px-12 mt-4 mb-8">
                      <transition-group name="list">
                        <div
                          v-for="(course, index) in filteredCourses"
                          :key="course[0].sco_user_id">
                          <course-row
                            :course="course"
                            :row-index="index"></course-row>
                        </div>
                      </transition-group>
                    </div>
                  </transition>
                </div>
                <div
                  v-else
                  class="py-12">
                  <p class="text-lg my-4 text-secondary">Sorry there are no matches for your filter settings.</p>
                  <button
                    @click.prevent="clearFilters"
                    class="btn btn-primary">
                    Clear Filters
                  </button>
                </div>
              </div>
              <div v-else>
                <h2 class="mt-4">You don't have any Training Courses.</h2>
              </div>
            </div>
            <div
              v-else-if="$store.state.learningZoneTab === 'ca'"
              key="caCourses">
              <div v-if="caCourses && caCourses.length">
                <h2 class="mt-4">Competency Assessments</h2>
                <p>Please remember to complete the Workbook before starting the Competency Assessment.</p>
                <div
                  v-if="filteredCourses.length > 0"
                  class="sm:-mx-12">
                  <transition
                    name="quick-fade"
                    mode="out-in">
                    <div
                      v-if="ScoLayout === 'ScoCard'"
                      class="flex flex-row flex-wrap px-2 md:px-6">
                      <transition-group name="list">
                        <div
                          v-for="course in filteredCourses"
                          :key="course[0].sco_user_id"
                          class="flex-none"
                          :class="cardWrapperClasses(course)">
                          <course-card :course="course"></course-card>
                        </div>
                      </transition-group>
                    </div>
                    <div
                      v-else
                      class="flex flex-col px-4 md:px-12 mt-4 mb-8">
                      <transition-group name="list">
                        <div
                          v-for="(course, index) in filteredCourses"
                          :key="course[0].sco_user_id">
                          <course-row
                            :course="course"
                            :row-index="index"></course-row>
                        </div>
                      </transition-group>
                    </div>
                  </transition>
                </div>
                <div
                  v-else
                  class="py-12">
                  <p class="text-lg my-4 text-secondary">Sorry there are no matches for your filter settings.</p>
                  <button
                    @click.prevent="clearFilters"
                    class="btn btn-primary">
                    Clear Filters
                  </button>
                </div>
              </div>
              <div v-else>
                <h2 class="mt-4">You don't have any Competency Assessments.</h2>
              </div>
            </div>
            <div
              v-else-if="$store.state.learningZoneTab === 'f2f'"
              key="f2fCourse">
              <div v-if="paperCourses && paperCourses.length > 0">
                <h2 class="mt-4">Face To Face Courses</h2>
                <p>
                  These are courses which had a paper assessment. They have no online content for you to access except
                  for the certificate if you passed/completed the course
                </p>

                <div
                  v-if="filteredPaperCourses.length > 0"
                  class="sm:-mx-12"
                  key="paperCourses">
                  <transition
                    name="quick-fade"
                    mode="out-in">
                    <div
                      v-if="ScoLayout === 'ScoCard'"
                      class="flex flex-row flex-wrap px-2 md:px-6"
                      key="paperCourseCards">
                      <transition-group name="list">
                        <div
                          v-for="paperCourse in filteredPaperCourses"
                          :key="paperCourse.paper_course_user_id"
                          class="flex-none w-full sm:w-1/2 lg:w-1/3 xl:w-1/4 px-2 md:px-6 py-1 sm:py-4">
                          <paper-course-card :paper-course="paperCourse"></paper-course-card>
                        </div>
                      </transition-group>
                    </div>
                    <div
                      v-else
                      class="flex flex-col px-4 md:px-12 mt-4"
                      key="paperCourseRows">
                      <transition-group name="list">
                        <div
                          v-for="(paperCourse, idx) in filteredPaperCourses"
                          :key="paperCourse.paper_course_user_id">
                          <paper-course-row
                            :paper-course="paperCourse"
                            :row-index="idx"></paper-course-row>
                        </div>
                      </transition-group>
                    </div>
                  </transition>
                </div>
                <div
                  v-else
                  class="py-12">
                  <p class="text-lg my-4 text-secondary">Sorry there are no matches for your filter settings.</p>
                  <button
                    @click.prevent="clearFilters"
                    class="btn btn-primary">
                    Clear Filters
                  </button>
                </div>
              </div>
              <div v-else>
                <h2 class="mt-4">You don't have any Face to Face Courses.</h2>
              </div>
            </div>
            <div
              v-else-if="$store.state.learningZoneTab === 'ec'"
              key="ecCourse">
              <div v-if="externalCourses && externalCourses.length > 0">
                <h2 class="mt-4">Paper-Based Assessments</h2>
                <p>
                  These are courses which had a paper assessment. They have no online content for you to access except
                  for the certificate if you passed/completed the course
                </p>

                <div
                  v-if="filteredExternalCourses.length > 0"
                  class="sm:-mx-12"
                  key="externalCourses">
                  <transition
                    name="quick-fade"
                    mode="out-in">
                    <div
                      v-if="ScoLayout === 'ScoCard'"
                      class="flex flex-row flex-wrap px-2 md:px-6"
                      key="externalCourseCards">
                      <transition-group name="list">
                        <div
                          v-for="externalCourse in filteredExternalCourses"
                          :key="externalCourse.external_course_user_id"
                          class="flex-none w-full sm:w-1/2 lg:w-1/3 xl:w-1/4 px-2 md:px-6 py-1 sm:py-4">
                          <external-course-card :external-course="externalCourse"></external-course-card>
                        </div>
                      </transition-group>
                    </div>
                    <div
                      v-else
                      class="flex flex-col px-4 md:px-12 mt-4"
                      key="externalCourseRows">
                      <transition-group name="list">
                        <div
                          v-for="(externalCourse, idx) in filteredExternalCourses"
                          :key="externalCourse.external_course_user_id">
                          <external-course-row
                            :external-course="externalCourse"
                            :row-index="idx"></external-course-row>
                        </div>
                      </transition-group>
                    </div>
                  </transition>
                </div>
                <div
                  v-else
                  class="py-12">
                  <p class="text-lg my-4 text-secondary">Sorry there are no matches for your filter settings.</p>
                  <button
                    @click.prevent="clearFilters"
                    class="btn btn-primary">
                    Clear Filters
                  </button>
                </div>
              </div>
              <div v-else>
                <h2 class="mt-4">You don't have any Paper-Based Assessments.</h2>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ApiService from "@/services/ApiService";
import slugify from "slugify";
import CourseCard from "@/components/courses/CourseCard.vue";
import CourseRow from "@/components/courses/CourseRow.vue";
import PaperCourseCard from "@/components/courses/PaperCourseCard.vue";
import PaperCourseRow from "@/components/courses/PaperCourseRow.vue";
import ExternalCourseCard from "@/components/courses/ExternalCourseCard.vue";
import ExternalCourseRow from "@/components/courses/ExternalCourseRow.vue";
import IconLegend from "@/components/icons/IconLegend.vue";

export default {
  name: "Courses",
  components: {
    CourseCard,
    CourseRow,
    PaperCourseCard,
    PaperCourseRow,
    ExternalCourseCard,
    ExternalCourseRow,
    IconLegend
  },
  data() {
    return {
      loaded: false,
      courses: null,
      paperCourses: null,
      externalCourses: null,
      filters: {
        title: null,
        status: null,
        deliverable: null
      },
      statusOptions: [
        { id: "Passed/Completed", label: "Passed/Completed" },
        { id: "Failed", label: "Further Training Required" },
        { id: "Started", label: "Started" },
        { id: "Not Started", label: "Not Started" }
      ],
      ScoLayout: "ScoCard",
      hideCertificateInLegend: 1
    };
  },
  created() {
    this.fetchContentList();
  },
  computed: {
    userGroups() {
      return this.$store.state.user.groups;
    },
    userSubgroups() {
      return this.$store.state.user.subgroups;
    },
    noContent() {
      return this.paperCourses.length === 0 && this.courses.length === 0 && this.externalCourses.length === 0;
    },
    isSolo() {
      return !this.$store.state.user.groups || this.$store.state.user.groups.length === 0;
    },
    filteredPaperCourses() {
      if (!this.filters.title && !this.filters.status) {
        return this.paperCourses;
      }

      let filteredPaperCourses = this.paperCourses;
      if (this.filters.title) {
        let escapedTitle = this.filters.title.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
        const pattern = new RegExp(escapedTitle, "i");
        filteredPaperCourses = filteredPaperCourses.filter((paperCourse) => {
          return pattern.test(paperCourse.paper_course_title);
        });
      }

      let filterStatus = this.filters.status;
      if (filterStatus) {
        filteredPaperCourses = filteredPaperCourses.filter((paperCourse) => {
          switch (filterStatus) {
            case "Passed/Completed":
              if (paperCourse.completion_status === "passed" || paperCourse.completion_status === "completed") {
                return true;
              }
              break;
            case "Failed":
              if (paperCourse.completion_status === "failed") {
                return true;
              }
              break;
            default:
              return true;
          }
        });
      }

      return filteredPaperCourses;
    },
    filteredExternalCourses() {
      if (!this.filters.title && !this.filters.status) {
        return this.externalCourses;
      }

      let filteredExternalCourses = this.externalCourses;
      if (this.filters.title) {
        let escapedTitle = this.filters.title.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
        const pattern = new RegExp(escapedTitle, "i");
        filteredExternalCourses = filteredExternalCourses.filter((externalCourse) => {
          return pattern.test(externalCourse.external_course_title);
        });
      }

      let filterStatus = this.filters.status;
      if (filterStatus) {
        filteredExternalCourses = filteredExternalCourses.filter((externalCourse) => {
          switch (filterStatus) {
            case "Passed/Completed":
              if (externalCourse.completion_status === "passed" || externalCourse.completion_status === "completed") {
                return true;
              }
              break;
            case "Failed":
              if (externalCourse.completion_status === "failed") {
                return true;
              }
              break;
            default:
              return true;
          }
        });
      }

      return filteredExternalCourses;
    },
    generalCourses() {
      let filteredCourses = this.courses;

      filteredCourses = filteredCourses.filter((course) => {
        return course[0].is_ca == 0;
      });

      return filteredCourses;
    },
    caCourses() {
      let filteredCourses = this.courses;
      filteredCourses = filteredCourses.filter((course) => {
        return course[0].is_ca == 1;
      });

      return filteredCourses;
    },
    filteredCourses() {
      if (!this.filters.title && !this.filters.status && !this.filters.deliverable) {
        if (this.$store.state.learningZoneTab === "ca") {
          return this.caCourses;
        } else {
          return this.generalCourses;
        }
      }
      let filteredCourses;
      if (this.$store.state.learningZoneTab === "ca") {
        filteredCourses = this.caCourses;
      } else {
        filteredCourses = this.generalCourses;
      }

      if (this.filters.deliverable) {
        filteredCourses = filteredCourses.filter((course) => {
          return course[0].deliverable_id == this.filters.deliverable;
        });
      }

      if (this.filters.title) {
        let escapedTitle = this.filters.title.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
        const pattern = new RegExp(escapedTitle, "i");
        filteredCourses = filteredCourses.filter((course) => {
          let hasAMatchingScoTitle = false;
          let i = 0;
          let limit = course.length;

          for (i; i < limit; i++) {
            if (pattern.test(course[i].title)) {
              hasAMatchingScoTitle = true;
              break;
            }
          }

          return hasAMatchingScoTitle;
        });
      }

      let filterStatus = this.filters.status;
      if (filterStatus) {
        filteredCourses = filteredCourses.filter((course) => {
          let i = 0;
          let limit = course.length;

          for (i; i < limit; i++) {
            switch (filterStatus) {
              case "Passed/Completed":
                if (course[i].lesson_status === "passed" || course[i].lesson_status === "completed") {
                  return true;
                }
                break;
              case "Failed":
                if (course[i].lesson_status === "failed") {
                  return true;
                }
                break;
              case "Started":
                if (course[i].lesson_status === "incomplete" || course[i].lesson_status === "browsed") {
                  return true;
                }
                break;
              case "Not Started":
                if (
                  course[i].lesson_status === "not attempted" ||
                  course[i].lesson_status === "" ||
                  course[i].lesson_status === null
                ) {
                  return true;
                }
                break;
              default:
                return true;
            }
          }

          return false;
        });
      }

      return filteredCourses;
    }
  },
  methods: {
    cardWrapperClasses(course) {
      switch (course.length) {
        case 1:
          return "w-full sm:w-1/2 lg:w-1/3 xl:w-1/4 px-2 md:px-6 py-1 sm:py-4";
        case 2:
          return "w-full xl:w-1/2 px-2 md:px-6 py-1 sm:py-4";
        default:
          return "w-full";
      }
    },
    toggleLayout() {
      if (this.ScoLayout === "ScoCard") {
        this.ScoLayout = "ScoRow";
      } else {
        this.ScoLayout = "ScoCard";
      }
    },
    clearFilters() {
      this.filters.title = null;
      this.filters.status = null;
    },
    fetchContentList() {
      ApiService.getContentList()
        .then((response) => {
          this.$store.commit("setGroups", response.data.groups);
          this.$store.commit("setSubgroups", response.data.subgroups);
          this.$store.commit("setVirtualGroups", response.data.virtualGroups);
          // these are not course objects they're arrays of sco's with extra data
          this.courses = response.data.courses;
          this.paperCourses = response.data.paperCourses;
          this.externalCourses = response.data.externalCourses;
          this.hideCertificateInLegend = response.data.hideCertificateInLegend;
          this.loaded = true;
        })
        .catch(() => {});
    },
    slugify(title) {
      return slugify(title, { lower: true, strict: true, locale: "en" });
    }
  }
};
</script>

<style scoped>
.list-enter-active,
.list-leave-active {
  transition: all 1s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
</style>
